import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { List, Modal } from "antd";

import { useClient } from "../../../../hooks/ClientProvider";
import Api from "../../../../services/api";
import { storageService } from "../../../../services/storageService";
import {
  formatHorizonDateTime,
  normalizeHorizonProcessorDateTime,
  toTimestampForSort,
} from "../../../../utils/formatDate";
import { notifyError } from "../../../../utils/notifications";
import LoadingElevaty from "../../../LoadingElevaty";
import * as S from "../../style";
import {
  BlockHistoryItem,
  HistoryBlockModalHandle,
  HistoryBlockModalProps,
} from "./types";

type WithIsLast<T extends React.ElementType> = React.ComponentType<
  React.ComponentProps<T> & { isLast?: boolean }
>;

const HistoryListItem = S.HistoryListItem as WithIsLast<
  typeof S.HistoryListItem
>;
const BlockSection = S.BlockSection as WithIsLast<typeof S.BlockSection>;

const HistoryBlockModal = forwardRef<
  HistoryBlockModalHandle,
  HistoryBlockModalProps
>(function HistoryBlockModal(
  { isModalVisible, closeModal, card, isBandeirado = false },
  ref
) {
  const [items, setItems] = useState<BlockHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const isPch = storageService.getIsPch();

  const { client } = useClient();
  const codigoProduto = client?.product?.codigoProduto;
  const cardId = card?.id || card?.card?.card?.id;

  const getData = useCallback(async () => {
    if (isBandeirado) return;

    setLoading(true);
    try {
      const { data } = await Api.get("card_block_history", {
        card_id: cardId,
        cod_product: codigoProduto,
      });
      const ajusteArray = (data as BlockHistoryItem[])
        .sort(
          (a, b) =>
            toTimestampForSort(
              normalizeHorizonProcessorDateTime(b.dataBloqueio)
            ) -
            toTimestampForSort(normalizeHorizonProcessorDateTime(a.dataBloqueio))
        )
        .splice(0, 5);
      setItems(ajusteArray);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erro ao buscar o histórico de bloqueio";
      notifyError(message);
    } finally {
      setLoading(false);
    }
  }, [cardId, codigoProduto, isBandeirado]);

  useImperativeHandle(ref, () => ({
    getData,
  }));

  useEffect(() => {
    if (isBandeirado || isPch) return;
    getData();
  }, [isModalVisible, isPch, isBandeirado, getData]);

  return (
    <Modal
      title="Histórico de Bloqueio"
      visible={isModalVisible}
      onCancel={closeModal}
      footer={null}
      width={600}
    >
      <S.WrapperSelectBlock data-testid="modal_historico_bloqueio">
        {!isBandeirado && loading && (
          <S.WrapperNewLoading>
            <LoadingElevaty width="30px" height="30px" duration="durFast" />
          </S.WrapperNewLoading>
        )}

        {!isBandeirado && !loading && (
          <List
            dataSource={items}
            renderItem={(item, index) => (
              <List.Item style={{ border: "none", padding: 0 }}>
                <HistoryListItem isLast={index === items.length - 1}>
                  <S.TimelineContainer>
                    <S.BlockSection>
                      <S.BlockHeader>Detalhes do Bloqueio</S.BlockHeader>

                      <S.DataGrid>
                        <S.GridLabel>Data de bloqueio:</S.GridLabel>
                        <S.GridValue>
                          {item.dataBloqueio
                            ? formatHorizonDateTime(item.dataBloqueio)
                            : ""}
                        </S.GridValue>

                        <S.GridLabel>Descrição:</S.GridLabel>
                        <S.GridValue>
                          {item.tipoBloqueioCartao?.descricao || ""}
                        </S.GridValue>

                        <S.GridLabel>Observação de Bloqueio:</S.GridLabel>
                        <S.GridValue>{item.observacao || ""}</S.GridValue>
                      </S.DataGrid>
                    </S.BlockSection>

                    <BlockSection isLast>
                      <S.BlockHeader>Detalhes do Desbloqueio</S.BlockHeader>

                      <S.DataGrid>
                        <S.GridLabel>Data de desbloqueio:</S.GridLabel>
                        <S.GridValue>
                          {item.dataDesbloqueio
                            ? formatHorizonDateTime(item.dataDesbloqueio)
                            : ""}
                        </S.GridValue>

                        <S.GridLabel>Observação de Desbloqueio:</S.GridLabel>
                        <S.GridValue>
                          {item.observacaoDesbloqueio || ""}
                        </S.GridValue>
                      </S.DataGrid>
                    </BlockSection>
                  </S.TimelineContainer>
                </HistoryListItem>
              </List.Item>
            )}
          />
        )}
      </S.WrapperSelectBlock>
    </Modal>
  );
});

export default memo(HistoryBlockModal);
